import React, { useState, useEffect } from "react";
import Select from "react-select";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/ClientManage.module.css";
import { 
  FiPlus, FiSearch, FiRefreshCw, FiEdit2, 
  FiX, FiTrash2, FiUsers, FiArrowLeft 
} from "react-icons/fi";

const DivSecManagement = () => {
  const [divSecs, setDivSecs] = useState([]);
  const [filteredDivSecs, setFilteredDivSecs] = useState([]);
  const [selectedDistrictFilter, setSelectedDistrictFilter] = useState(null);
  const [selectedProvinceFilter, setSelectedProvinceFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [currentDivSec, setCurrentDivSec] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [availableDSForCustomer, setAvailableDSForCustomer] = useState([]);
  const [formData, setFormData] = useState({
    ds_name: "",
    ds_district: "",
    ds_province: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [updateStatus, setUpdateStatus] = useState({
    success: null,
    message: "",
  });
  const [customerUpdateStatus, setCustomerUpdateStatus] = useState({
    success: null,
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterDivSecs();
  }, [divSecs, selectedDistrictFilter, selectedProvinceFilter, searchTerm]);

  useEffect(() => {
    if (divSecs.length > 0) {
      const uniqueDistricts = [...new Set(divSecs.map(ds => ds.ds_district))];
      setDistricts(uniqueDistricts.map(district => ({ 
        value: district, 
        label: district 
      })));

      const uniqueProvinces = [...new Set(divSecs.map(ds => ds.ds_province))];
      setProvinces(uniqueProvinces.map(province => ({ 
        value: province, 
        label: province 
      })));
    }
  }, [divSecs]);

  const filterDivSecs = () => {
    let filtered = [...divSecs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ds) =>
          ds.ds_name?.toLowerCase().includes(term) ||
          ds.ds_district?.toLowerCase().includes(term) ||
          ds.ds_province?.toLowerCase().includes(term)
      );
    }

    if (selectedDistrictFilter) {
      filtered = filtered.filter(
        (ds) => ds.ds_district === selectedDistrictFilter.value
      );
    }

    if (selectedProvinceFilter) {
      filtered = filtered.filter(
        (ds) => ds.ds_province === selectedProvinceFilter.value
      );
    }

    setFilteredDivSecs(filtered);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/divisional-secretariats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch divisional secretariats");

      const data = await response.json();
      
      const formattedData = data.map(item => ({
        id: item.id,
        ds_name: item.ds_name,
        ds_district: item.ds_district,
        ds_province: item.ds_province
      }));

      setDivSecs(Array.isArray(formattedData) ? formattedData : []);
      setFilteredDivSecs(Array.isArray(formattedData) ? formattedData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchCustomersByDS = async (dsId) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-customers-by-ds/${dsId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch customers");

      const data = await response.json();
      
      // Handle both array and object responses with multiple field name variations
      let formattedCustomers = [];
      if (Array.isArray(data)) {
        formattedCustomers = data.map(customer => ({
          id: customer.customer_id || customer.id || "-",
          name: customer.customer_name || customer.name || "-",
          current_ds_id: customer.customer_ds_id || customer.ds_id || dsId
        }));
      } else if (typeof data === 'object' && data !== null) {
        formattedCustomers = [{
          id: data.customer_id || data.id || "-",
          name: data.customer_name || data.name || "-",
          current_ds_id: data.customer_ds_id || data.ds_id || dsId
        }];
      }

      setCustomers(formattedCustomers);
      setShowCustomerModal(true);
    } catch (error) {
      console.error("Error fetching customers:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const updateCustomerDS = async (customerId, newDsId) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/update-customer-ds/${customerId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ds_id: newDsId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update customer's DS");
      }

      setCustomerUpdateStatus({
        success: true,
        message: "Customer's DS updated successfully!",
      });

      setCustomers(prevCustomers => 
        prevCustomers.map(customer => 
          customer.id === customerId 
            ? { ...customer, current_ds_id: newDsId } 
            : customer
        )
      );

      setTimeout(() => {
        setCustomerUpdateStatus({ success: null, message: "" });
      }, 1500);
    } catch (error) {
      console.error("Error updating customer's DS:", error);
      setCustomerUpdateStatus({
        success: false,
        message: error.message || "Failed to update customer's DS",
      });
    }
  };

  const fetchAvailableDS = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/divisional-secretariats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch DS divisions");

      const data = await response.json();
      setAvailableDSForCustomer(data.map(ds => ({
        value: ds.id,
        label: `${ds.ds_name} (${ds.ds_district})`
      })));
    } catch (error) {
      console.error("Error fetching DS divisions:", error);
    }
  };

  const handleCreateDivSec = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setUpdateStatus({ success: null, message: "" });

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/create-ds`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ds_name: formData.ds_name,
            ds_district: formData.ds_district,
            ds_province: formData.ds_province,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create divisional secretariat");
      }

      setUpdateStatus({
        success: true,
        message: "Divisional secretariat created successfully!",
      });

      await fetchData();

      setTimeout(() => {
        resetForm();
        setShowCreateModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error creating divisional secretariat:", error);
      setUpdateStatus({
        success: false,
        message: error.message || "Failed to create divisional secretariat",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDivSec = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setUpdateStatus({ success: null, message: "" });

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/update-ds/${currentDivSec.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ds_name: formData.ds_name,
            ds_district: formData.ds_district,
            ds_province: formData.ds_province,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update divisional secretariat");
      }

      setUpdateStatus({
        success: true,
        message: "Divisional secretariat updated successfully!",
      });

      await fetchData();

      setTimeout(() => {
        resetForm();
        setShowUpdateModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error updating divisional secretariat:", error);
      setUpdateStatus({
        success: false,
        message: error.message || "Failed to update divisional secretariat",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDivSec = async () => {
    setIsSubmitting(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/delete-ds/${currentDivSec.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete divisional secretariat");
      }

      await fetchData();
      setShowDeleteConfirmModal(false);
    } catch (error) {
      console.error("Error deleting divisional secretariat:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.ds_name?.trim()) {
      errors.ds_name = "DS name is required";
    }

    if (!formData.ds_district?.trim()) {
      errors.ds_district = "District is required";
    }

    if (!formData.ds_province?.trim()) {
      errors.ds_province = "Province is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      ds_name: "",
      ds_district: "",
      ds_province: "",
    });
    setFormErrors({});
    setUpdateStatus({ success: null, message: "" });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openUpdateModal = (divSec) => {
    setCurrentDivSec(divSec);
    setFormData({
      ds_name: divSec.ds_name || "",
      ds_district: divSec.ds_district || "",
      ds_province: divSec.ds_province || "",
    });
    setShowUpdateModal(true);
  };

  const openViewModal = (divSec) => {
    setCurrentDivSec(divSec);
    fetchCustomersByDS(divSec.id); // Fetch customers when opening view modal
    setShowViewModal(true);
  };

  const openDeleteConfirmModal = (divSec) => {
    setCurrentDivSec(divSec);
    setShowDeleteConfirmModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleDistrictChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      ds_district: selected ? selected.value : "",
    }));

    if (formErrors.ds_district) {
      setFormErrors((prev) => ({
        ...prev,
        ds_district: null,
      }));
    }
  };

  const handleProvinceChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      ds_province: selected ? selected.value : "",
    }));

    if (formErrors.ds_province) {
      setFormErrors((prev) => ({
        ...prev,
        ds_province: null,
      }));
    }
  };

  const handleDistrictFilterChange = (selected) => {
    setSelectedDistrictFilter(selected);
  };

  const handleProvinceFilterChange = (selected) => {
    setSelectedProvinceFilter(selected);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSelectedDistrictFilter(null);
    setSelectedProvinceFilter(null);
    setSearchTerm("");
  };

  const renderActionsCell = (divSec) => (
    <td className={styles.actionsCell}>
      <button
        className={styles.viewCustomersBtn}
        title="View Customers"
        onClick={(e) => {
          e.stopPropagation();
          setCurrentDivSec(divSec);
          fetchCustomersByDS(divSec.id);
          fetchAvailableDS();
        }}
      >
        <FiUsers />
      </button>
      <button
        className={styles.updateBtn}
        title="Edit DS"
        onClick={(e) => {
          e.stopPropagation();
          openUpdateModal(divSec);
        }}
      >
        <FiEdit2 />
      </button>
      <button
        className={styles.deleteBtn}
        title="Delete DS"
        onClick={(e) => {
          e.stopPropagation();
          openDeleteConfirmModal(divSec);
        }}
      >
        <FiTrash2 />
      </button>
    </td>
  );

  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading Divisional Secretariats...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Divisional Secretariat Management</h1>
          <button
            className={styles.refreshButton}
            disabled={isRefreshing}
            onClick={fetchData}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.controlsLeft}>
            <button className={styles.createBtn} onClick={openCreateModal}>
              <FiPlus /> Create DS
            </button>
          </div>

          <div className={styles.controlsRight}>
            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search DS divisions..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredDivSecs.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                Showing {filteredDivSecs.length} of {divSecs.length} DS divisions
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th className={styles.numberColumn}>DS Name</th>
                      <th className={styles.clientColumn}>District</th>
                      <th className={styles.typeColumn}>Province</th>
                      <th className={styles.actionsColumn}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDivSecs.map((divSec) => (
                      <tr
                        key={`divsec-${divSec.id}`}
                        className={styles.clickableRow}
                        onClick={() => openViewModal(divSec)}
                      >
                        <td className={styles.numberCell}>{divSec.ds_name || "-"}</td>
                        <td className={styles.clientCell}>{divSec.ds_district || "-"}</td>
                        <td className={styles.typeCell}>{divSec.ds_province || "-"}</td>
                        {renderActionsCell(divSec)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>No divisional secretariats found matching your criteria.</p>
              {(selectedDistrictFilter || selectedProvinceFilter || searchTerm) && (
                <button className={styles.clearFilters} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create DS Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Create New Divisional Secretariat</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowCreateModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <form className={styles.updateForm} onSubmit={handleCreateDivSec}>
                {updateStatus.message && (
                  <div
                    className={
                      updateStatus.success
                        ? styles.successMessage
                        : styles.errorMessage
                    }
                  >
                    {updateStatus.message}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="ds_name">DS Name*</label>
                  <input
                    type="text"
                    id="ds_name"
                    name="ds_name"
                    value={formData.ds_name}
                    onChange={handleInputChange}
                    className={formErrors.ds_name ? styles.errorInput : ""}
                    required
                  />
                  {formErrors.ds_name && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="ds_district">District*</label>
                  <Select
                    id="ds_district"
                    name="ds_district"
                    options={districts}
                    value={districts.find(d => d.value === formData.ds_district) || null}
                    onChange={handleDistrictChange}
                    styles={customSelectStyles}
                    className={formErrors.ds_district ? styles.errorInput : ""}
                    placeholder="Select district"
                  />
                  {formErrors.ds_district && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_district}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="ds_province">Province*</label>
                  <Select
                    id="ds_province"
                    name="ds_province"
                    options={provinces}
                    value={provinces.find(p => p.value === formData.ds_province) || null}
                    onChange={handleProvinceChange}
                    styles={customSelectStyles}
                    className={formErrors.ds_province ? styles.errorInput : ""}
                    placeholder="Select province"
                  />
                  {formErrors.ds_province && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_province}
                    </span>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating..." : "Create DS"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update DS Modal */}
      {showUpdateModal && currentDivSec && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Update Divisional Secretariat ({currentDivSec.ds_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowUpdateModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <form className={styles.updateForm} onSubmit={handleUpdateDivSec}>
                {updateStatus.message && (
                  <div
                    className={
                      updateStatus.success
                        ? styles.successMessage
                        : styles.errorMessage
                    }
                  >
                    {updateStatus.message}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="ds_name">DS Name*</label>
                  <input
                    type="text"
                    id="ds_name"
                    name="ds_name"
                    value={formData.ds_name}
                    onChange={handleInputChange}
                    className={formErrors.ds_name ? styles.errorInput : ""}
                    required
                  />
                  {formErrors.ds_name && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="ds_district">District*</label>
                  <input
                    type="text"
                    id="ds_district"
                    name="ds_district"
                    value={formData.ds_district}
                    onChange={handleInputChange}
                    className={formErrors.ds_district ? styles.errorInput : ""}
                    required
                  />
                  {formErrors.ds_district && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_district}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="ds_province">Province*</label>
                  <input
                    type="text"
                    id="ds_province"
                    name="ds_province"
                    value={formData.ds_province}
                    onChange={handleInputChange}
                    className={formErrors.ds_province ? styles.errorInput : ""}
                    required
                  />
                  {formErrors.ds_province && (
                    <span className={styles.errorMessage}>
                      {formErrors.ds_province}
                    </span>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowUpdateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update DS"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View DS Modal */}
      {showViewModal && currentDivSec && (
        <div className={styles.modalOverlay}>
          <div className={styles.largeModalContent}>
            <div className={styles.modalHeader}>
              <h2>Divisional Secretariat Details ({currentDivSec.ds_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowViewModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.invoiceSummary}>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>DS Name:</span>
                    <span className={styles.summaryValue}>
                      {currentDivSec.ds_name || "-"}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>District:</span>
                    <span className={styles.summaryValue}>
                      {currentDivSec.ds_district || "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Province:</span>
                    <span className={styles.summaryValue}>
                      {currentDivSec.ds_province || "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Customers:</span>
                    <div className={styles.customerList}>
                      {customers.length > 0 ? (
                        <ul>
                          {customers.map(customer => (
                            <li key={`view-customer-${customer.id}`}>
                              {customer.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className={styles.summaryValue}>No customers found</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.viewCustomersButton}
                  onClick={() => {
                    setShowViewModal(false);
                    setCurrentDivSec(currentDivSec);
                    fetchCustomersByDS(currentDivSec.id);
                    fetchAvailableDS();
                  }}
                >
                  <FiUsers /> Manage Customers
                </button>
                <button
                  className={styles.editButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openUpdateModal(currentDivSec);
                  }}
                >
                  <FiEdit2 /> Edit DS
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openDeleteConfirmModal(currentDivSec);
                  }}
                >
                  <FiTrash2 /> Delete DS
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customers Modal */}
      {showCustomerModal && currentDivSec && (
        <div className={styles.modalOverlay}>
          <div className={styles.largeModalContent}>
            <div className={styles.modalHeader}>
              <button
                className={styles.backButton}
                onClick={() => setShowCustomerModal(false)}
              >
                <FiArrowLeft />
              </button>
              <h2>
                Customers in {currentDivSec.ds_name} Division
                <span className={styles.customerCount}>
                  ({customers.length} customer{customers.length !== 1 ? 's' : ''})
                </span>
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowCustomerModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              {customerUpdateStatus.message && (
                <div
                  className={
                    customerUpdateStatus.success
                      ? styles.successMessage
                      : styles.errorMessage
                  }
                >
                  {customerUpdateStatus.message}
                </div>
              )}

              {customers.length > 0 ? (
                <div className={styles.tableScrollWrapper}>
                  <table className={styles.customersTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Current DS</th>
                        <th>Change DS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer) => (
                        <tr key={`customer-row-${customer.id}`}>
                          <td>{customer.name}</td>
                          <td>{currentDivSec.ds_name}</td>
                          <td className={styles.changeDsCell}>
                            <Select
                              options={availableDSForCustomer.filter(
                                ds => ds.value !== customer.current_ds_id
                              )}
                              onChange={(selected) => 
                                updateCustomerDS(customer.id, selected.value)
                              }
                              placeholder="Select new DS"
                              styles={customSelectStyles}
                              className={styles.dsSelect}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noCustomers}>
                  <p>No customers found in this divisional secretariat.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && currentDivSec && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmationModal}>
            <div className={styles.modalHeader}>
              <h2>Confirm Deletion</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.confirmationContent}>
                <p className={styles.confirmationText}>
                  Are you sure you want to delete Divisional Secretariat:{" "}
                  <strong>{currentDivSec.ds_name}</strong>?
                </p>
                <p className={styles.confirmationWarning}>
                  This action cannot be undone.
                </p>
              </div>

              <div className={styles.confirmationActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => setShowDeleteConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={styles.dangerButton}
                  onClick={handleDeleteDivSec}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Deleting..." : "Delete DS"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "40px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#e0e7ff" : "white",
    color: state.isSelected ? "#1e40af" : "#1e293b",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#1e40af",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#94a3b8",
    "&:hover": {
      color: "#64748b",
    },
  }),
};

export default DivSecManagement;